// Appels HTTP typés vers /api/admin/* — dashboard cross-tenant Hsasoft
// (Lot 8.6.2 backend / 8.6.3 frontend).
//
// Une seule route pour l'instant : `GET /api/admin/overview`.
//
// Garde côté backend = email allow-list `HSASOFT_ADMIN_EMAILS`. La page
// frontend ne fait PAS de check supplémentaire — on laisse le 403/404
// remonter et on affiche un message d'erreur propre. Cela garantit que
// l'autorité reste côté backend (pattern aligné sur les autres pages
// admin du projet, p. ex. AuditPage).
//
// Les types ci-dessous sont alignés sur app/schemas/admin.py.

import { api } from "./client";
import type { UUID } from "./types";
import type { SubscriptionStatus } from "./billing";

// ---- Sous-objets --------------------------------------------------------

export type AdminTenantsByStatus = {
  /** Tenants avec une subscription ACTIVE. */
  active: number;
  /** Tenants avec une subscription PAST_DUE (en grâce). */
  past_due: number;
  /** Tenants avec une subscription CANCELED (peut chevaucher d'autres états). */
  canceled: number;
  /** Tenants en trial actif et SANS subscription ACTIVE/PAST_DUE. */
  trial: number;
  /** Tenants sans aucune subscription effective et hors trial — en pratique
   *  ce sont des comptes "free" abandonnés ou expirés. INCOMPLETE compté ici. */
  no_subscription: number;
  total: number;
};

export type AdminUnpaidBucket = {
  count: number;
  /** Decimal sérialisé en string (centimes / euros, dépend du back). */
  total_amount_ttc: string;
  currency: string;
};

export type AdminTenantRow = {
  id: UUID;
  name: string;
  slug: string;
  /** Slug du plan rattaché au Tenant (vérité courante du droit). */
  plan_slug: string | null;
  /** Status de la subscription la plus récente — null si pas encore souscrit. */
  subscription_status: SubscriptionStatus | null;
  trial_ends_at: string | null;
  created_at: string;
};

export type AdminUnpaidBiRow = {
  id: UUID;
  reference: string;
  tenant_id: UUID;
  tenant_name: string;
  amount_ttc: string;
  currency: string;
  due_date: string; // YYYY-MM-DD
  /** today - due_date en jours. Négatif = échéance dans le futur. */
  days_overdue: number;
};

// ---- Réponse de /api/admin/overview ------------------------------------

export type AdminOverview = {
  generated_at: string;
  /** MRR en centimes — somme des plans actifs sur la devise dominante. */
  mrr_cents: number;
  mrr_currency: string;
  active_subscriptions: number;
  tenants: AdminTenantsByStatus;
  unpaid_bi: AdminUnpaidBucket;
  /** Nombre de subscriptions CANCELED dans les 30 derniers jours. */
  churn_30d: number;
  /** 10 derniers tenants créés (tri created_at desc). */
  latest_tenants: AdminTenantRow[];
  /** 10 BI pending les plus anciennes (tri due_date asc). */
  latest_unpaid_bi: AdminUnpaidBiRow[];
};

// ---- API ----------------------------------------------------------------

export const adminApi = {
  overview: () => api.get<AdminOverview>("/api/admin/overview"),
};
